import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckSquare, Square, ListChecks, Plus } from 'lucide-react'
import {
  PageHeader, SectionCard, EmptyState, DataTable, Badge, Button,
  ListToolbar, KpiTile, KpiGrid, TaskModal, type Column, type BadgeTone,
} from '@/components/ui'
import { useStore, useHasPermission } from '@/store/useStore'
import { contactName } from '@/lib/selectors'
import { date, daysUntil, todayIso} from '@/lib/format'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { isOwnedBy } from '@/lib/actionOwnership'
import type {
  ActionItem, Dataset, EntityRef, Priority,
} from '@/types/entities'
import './Tasks.css'

/* ---- local helpers (inlined; no shared-file edits) ---- */

const PRIORITY_TONE: Record<Priority, BadgeTone> = {
  Critical: 'alert', High: 'warn', Medium: 'info', Low: 'neutral',
}
const PRIORITY_RANK: Record<Priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 }

const STATUS_TONE: Record<string, BadgeTone> = {
  Open: 'neutral', 'In Progress': 'info', Blocked: 'alert', Closed: 'ok',
}

// "Mine" matches action-item owner against the signed-in user's display
// name (was the prototype's hardcoded Jason Lopez).

/** Resolve a polymorphic ref to a route, or undefined if not navigable. */
function routeForRef(d: Dataset, ref: EntityRef): string | undefined {
  switch (ref.kind) {
    case 'account': return `/accounts/${ref.id}`
    case 'property': return `/properties/${ref.id}`
    case 'contact': return `/contacts/${ref.id}`
    case 'opportunity': return `/opportunities/${ref.id}`
    case 'fund': return `/funds/${ref.id}`
    case 'track': {
      const t = d.tracks.find((x) => x.id === ref.id)
      return t ? `/accounts/${t.accountId}` : undefined
    }
    default: return undefined
  }
}

/** Best display label for a ref — use seeded label, else resolve the entity name. */
function labelForRef(d: Dataset, ref: EntityRef): string {
  if (ref.label) return ref.label
  switch (ref.kind) {
    case 'account': return d.accounts.find((a) => a.id === ref.id)?.name ?? ref.id
    case 'property': return d.properties.find((p) => p.id === ref.id)?.name ?? ref.id
    case 'contact': {
      const c = d.contacts.find((x) => x.id === ref.id)
      return c ? contactName(c) : ref.id
    }
    case 'track': return d.tracks.find((t) => t.id === ref.id)?.name ?? ref.id
    case 'fund': return d.funds.find((f) => f.id === ref.id)?.name ?? ref.id
    case 'opportunity': return d.opportunities.find((o) => o.id === ref.id)?.name ?? ref.id
    default: return ref.id
  }
}

const VIEWS = [
  { id: 'all', label: 'All Open' },
  { id: 'mine', label: 'Mine' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'high', label: 'High Priority' },
]

export default function Tasks() {
  const data = useStore((s) => s.data)
  const patch = useStore((s) => s.patch)
  const allItems = useStore((s) => s.data.actionItems)
  const navigate = useNavigate()
  const me = useCurrentUser()
  const ME = me.displayName
  const MY_EMAIL = me.email.toLowerCase()
  const isMine = (a: ActionItem) => isOwnedBy(a, me.email, ME)
  // Visibility (Chauncey 2026-07-13): regular users see ONLY their own
  // action items; leadership — crm.manage (admins) or crm.financials.view
  // (asset-management leadership: Chauncey, Jason, Robert Masterson) —
  // sees the whole book.
  const canManageAll = useHasPermission('crm.manage')
  const canSeeFinancials = useHasPermission('crm.financials.view')
  const seeAll = canManageAll || canSeeFinancials

  const [view, setView] = useState('all')
  const [search, setSearch] = useState('')
  // '?new=1' deep link (TopBar QuickAdd) auto-opens the create modal.
  const [searchParams, setSearchParams] = useSearchParams()
  const [newOpen, setNewOpen] = useState(false)
  useEffect(() => {
    if (searchParams.get('new')) {
      setNewOpen(true)
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  function toggle(item: ActionItem) {
    patch('actionItems', item.id, item.status === 'Closed'
      ? { status: 'Open', closedAt: undefined }
      : { status: 'Closed', closedAt: todayIso() })
  }

  /* Base set: open items — the whole book for leadership, own items only
     for everyone else. */
  const openItems = useMemo(
    () => allItems.filter((a) => a.status !== 'Closed' && (seeAll || isMine(a))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allItems, seeAll, ME, MY_EMAIL],
  )

  /* Stat strip */
  const stats = useMemo(() => {
    const overdue = openItems.filter((a) => {
      const du = daysUntil(a.dueDate)
      return du != null && du < 0
    }).length
    const dueSoon = openItems.filter((a) => {
      const du = daysUntil(a.dueDate)
      return du != null && du >= 0 && du <= 7
    }).length
    const critical = openItems.filter((a) => a.priority === 'Critical' || a.priority === 'High').length
    const mine = openItems.filter(isMine).length
    return { total: openItems.length, overdue, dueSoon, critical, mine }
  }, [openItems, ME, MY_EMAIL])

  /* View + search filter, then sort: overdue first, then by due date, then priority. */
  const rows = useMemo(() => {
    let set = openItems
    if (view === 'mine') set = set.filter(isMine)
    else if (view === 'overdue') set = set.filter((a) => {
      const du = daysUntil(a.dueDate); return du != null && du < 0
    })
    else if (view === 'high') set = set.filter((a) => a.priority === 'Critical' || a.priority === 'High')

    const q = search.trim().toLowerCase()
    if (q) {
      set = set.filter((a) =>
        a.title.toLowerCase().includes(q)
        || (a.owner ?? '').toLowerCase().includes(q)
        || (a.attachedTo.label ?? '').toLowerCase().includes(q)
        || (a.source ?? '').toLowerCase().includes(q))
    }

    return [...set].sort((a, b) => {
      const da = daysUntil(a.dueDate)
      const db = daysUntil(b.dueDate)
      const aOver = da != null && da < 0
      const bOver = db != null && db < 0
      if (aOver !== bOver) return aOver ? -1 : 1 // overdue surfaces to top
      // both overdue OR both not: order by due date (nulls last)
      const ad = a.dueDate ?? '9999-12-31'
      const bd = b.dueDate ?? '9999-12-31'
      if (ad !== bd) return ad < bd ? -1 : 1
      return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
    })
  }, [openItems, view, search, ME, MY_EMAIL])

  const columns: Column<ActionItem>[] = [
    {
      key: 'done', header: '', width: 40, align: 'center',
      render: (a) => (
        <button
          className="tk-check"
          onClick={(e) => { e.stopPropagation(); toggle(a) }}
          aria-label={a.status === 'Closed' ? 'Reopen' : 'Close'}
        >
          {a.status === 'Closed' ? <CheckSquare size={17} className="tk-check-on" /> : <Square size={17} />}
        </button>
      ),
    },
    {
      key: 'title', header: 'Action Item', sortValue: (a) => a.title,
      render: (a) => (
        <div className="tk-title-cell">
          <span className="tk-title">{a.title}</span>
          {a.description && <span className="tk-desc">{a.description}</span>}
        </div>
      ),
    },
    {
      key: 'entity', header: 'Linked To', width: 200,
      sortValue: (a) => labelForRef(data, a.attachedTo),
      render: (a) => {
        const to = routeForRef(data, a.attachedTo)
        const label = labelForRef(data, a.attachedTo)
        return (
          <div className="tk-entity-cell">
            {to ? (
              <Link to={to} className="tk-entity-link" onClick={(e) => e.stopPropagation()}>{label}</Link>
            ) : (
              <span className="tk-entity-link">{label}</span>
            )}
            <span className="tk-entity-kind label">{a.attachedTo.kind}</span>
          </div>
        )
      },
    },
    {
      key: 'priority', header: 'Priority', width: 96, align: 'center',
      sortValue: (a) => PRIORITY_RANK[a.priority],
      render: (a) => <Badge tone={PRIORITY_TONE[a.priority]} variant="soft" dot>{a.priority}</Badge>,
    },
    {
      key: 'status', header: 'Status', width: 116, align: 'center',
      sortValue: (a) => a.status,
      render: (a) => <Badge tone={STATUS_TONE[a.status] ?? 'neutral'} variant="soft">{a.status}</Badge>,
    },
    {
      key: 'owner', header: 'Owner', width: 150, sortValue: (a) => a.owner ?? '',
      render: (a) => <span className="tk-muted">{a.owner ?? '—'}</span>,
    },
    {
      key: 'due', header: 'Due Date', width: 138, align: 'right',
      sortValue: (a) => a.dueDate ?? '9999-12-31',
      render: (a) => {
        if (!a.dueDate) return <span className="dim">—</span>
        const du = daysUntil(a.dueDate)
        const late = du != null && du < 0
        const soon = du != null && du >= 0 && du <= 7
        return (
          <span className={`num tk-due${late ? ' tk-due--late' : soon ? ' tk-due--soon' : ''}`}>
            {date(a.dueDate, 'short')}
            {du != null && (
              <span className="tk-due-rel">{du < 0 ? `${-du}d late` : du === 0 ? 'today' : `in ${du}d`}</span>
            )}
          </span>
        )
      },
    },
    {
      key: 'source', header: 'Source', width: 160, sortValue: (a) => a.source ?? '',
      render: (a) => <span className="tk-muted">{a.source ?? '—'}</span>,
    },
  ]

  return (
    <div className="tk">
      <PageHeader
        eyebrow="Execution"
        title={<>Open <b>Action Items</b></>}
        sub={`${stats.total} open follow-ups ${seeAll ? 'across every account' : 'assigned to you'} — ${stats.overdue} overdue, ${stats.dueSoon} due within a week. Check an item to close it.`}
        actions={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setNewOpen(true)}>New task</Button>}
      />

      <TaskModal open={newOpen} onClose={() => setNewOpen(false)} />

      <KpiGrid cols={4}>
        <KpiTile label="Open Items" value={stats.total} sub="across all books" />
        <KpiTile label="Overdue" value={stats.overdue} accent={stats.overdue > 0} sub="past due date" />
        <KpiTile label="Due This Week" value={stats.dueSoon} sub="next 7 days" />
        <KpiTile label="Critical / High" value={stats.critical} sub="top priority" />
      </KpiGrid>

      <SectionCard pad={false}>
        <ListToolbar
          views={VIEWS}
          activeView={view}
          onView={setView}
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search items, owners, entities…"
          count={rows.length}
        />
        {rows.length === 0 ? (
          <div className="tk-empty-wrap">
            <EmptyState
              icon={<ListChecks size={34} />}
              title="No open items match this view"
              hint="Follow-ups from reviews, reporting windows, and operations land here. Try a different view or clear the search."
            />
          </div>
        ) : (
          <DataTable<ActionItem>
            columns={columns}
            rows={rows}
            getRowKey={(a) => a.id}
            onRowClick={(a) => {
              const to = routeForRef(data, a.attachedTo)
              if (to) navigate(to)
            }}
            dense
          />
        )}
      </SectionCard>
    </div>
  )
}
